import { Check, SettingsOutlined } from "@mui/icons-material";
import { Divider, IconButton, Menu, ToggleButton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../../../contexts/Staffing/UserSettingsContext";
import { useWeekIndex } from "../../../contexts/WeekIndexProvider";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useSaveAsDefaultEnabled } from "../../../hooks/useSaveAsDefaultEnabled";
import { NEXT_7_DAYS_VALUE, resolveWeekStartDaySelection, useWeekStartDayOptions } from "../../../hooks/useWeekStartDayOptions";
import { INK } from "../../../theme/tokens";
import { DailyViewOption, HppdSeparationType, LocationMode, type StaffingSettings, WeeklyGroupingType, WeeklyViewOption } from "../../../utils/models";
import { SaveAsDefaultDialog } from "../../SaveAsDefault/SaveAsDefaultDialog";
import { SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import { SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { InfoIconWithTooltip } from "../../UIToolkit/molecules/InfoIconWithTooltip";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";

type StaffingSettingsPanelProps = {
	mode: "daily" | "weekly";
	/**
	 * Annie walkthrough anchor (`context/standards/frontend.md`). Explicit because this component doesn't
	 * spread rest props, and TypeScript exempts hyphenated JSX attributes from excess-property checking —
	 * so an unknown `data-*` prop is dropped silently rather than failing the build.
	 *
	 * Passed by the caller, not hardcoded here: the Day Schedule and the People view render this same
	 * panel, and an anchor on all four call sites would spotlight it on pages the step's copy isn't about.
	 */
	"data-annie-target"?: string;
};

export const StaffingSettingsPanel: FC<StaffingSettingsPanelProps> = ({ mode, "data-annie-target": annieTarget }) => {
	const { t } = useTranslation();
	const weeklyGapsViewEnabled = useFeatureFlag(FEATURE_FLAGS.weeklyGapsView);
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const saveAsDefaultEnabled = useSaveAsDefaultEnabled();
	// When job management is on the product groups exclusively by job, so role / role-group grouping
	// options are shown disabled (the active value is forced to Jobs upstream in UserSettingsProvider).
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { settings, updateSettings, isLoading } = useUserSettingsContext();
	const { hospitalData, chosenDepartment, getWeekIndexByDate } = useHospitalData();
	const { viewStartDay, setViewStartDay, setWeekIndex } = useWeekIndex();
	const { track } = useAnalytics();
	const enabledJobs = useJobs();
	const [anchorEl, setAnchorEl] = useState<HTMLElement>();
	const [localSettings, setLocalSettings] = useState<StaffingSettings>(settings);
	const [localViewStartDay, setLocalViewStartDay] = useState<number>(viewStartDay);
	const [isHovered, setIsHovered] = useState<boolean>(false);
	const [saveAsDefault, setSaveAsDefault] = useState<boolean>(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

	const weekStartDayOptions = useWeekStartDayOptions();

	const applyWeekStartDay = useCallback((): void => {
		if (localViewStartDay === viewStartDay) return;
		const isNext7Days = localViewStartDay === NEXT_7_DAYS_VALUE;
		setViewStartDay(resolveWeekStartDaySelection(localViewStartDay, chosenDepartment.timezone));
		// "Next 7 days" re-anchors the view to the week containing today (dept tz).
		if (isNext7Days) setWeekIndex(getWeekIndexByDate(DateTime.now().setZone(chosenDepartment.timezone)));
		track("staffing_week_start_changed", {
			week_start_day: isNext7Days ? "next_7_days" : localViewStartDay,
			department_default: chosenDepartment.week_start_day,
		});
	}, [localViewStartDay, viewStartDay, setViewStartDay, setWeekIndex, getWeekIndexByDate, track, chosenDepartment.timezone, chosenDepartment.week_start_day]);

	// Closing without saving must not leave a staged choice behind. The `settings`-keyed effects below
	// only re-sync when the SOURCE changes, so a cancelled edit would otherwise still be sitting in local
	// state the next time the panel opened.
	const closePanel = useCallback((): void => {
		setLocalSettings(settings);
		setLocalViewStartDay(viewStartDay);
		setSaveAsDefault(false);
		setAnchorEl(undefined);
	}, [settings, viewStartDay]);

	// Day/Week settings never write appearance fields, including a department-default save.
	const settingsToSave = useCallback((): Partial<StaffingSettings> => {
		return { ...localSettings, jobColors: undefined };
	}, [localSettings]);

	const onSubmit = useCallback(async (): Promise<void> => {
		if (saveAsDefault) {
			setShowConfirmDialog(true);
			return;
		}
		applyWeekStartDay();
		// Only after the save succeeds: `updateSettings` can throw, and switching the view first left the
		// user looking at People while their settings had not been persisted.
		await updateSettings(settingsToSave());
		setAnchorEl(undefined);
	}, [updateSettings, settingsToSave, saveAsDefault, applyWeekStartDay]);

	const onConfirmSaveAsDefault = useCallback(async (): Promise<void> => {
		setShowConfirmDialog(false);
		applyWeekStartDay();
		await updateSettings(settingsToSave(), true);
		setAnchorEl(undefined);
	}, [updateSettings, settingsToSave, applyWeekStartDay]);

	// Re-sync from the source only while the panel is closed: an action inside the open panel that saves right away
	// ("Use department colors") must not throw away the other edits the user hasn't submitted yet.
	useEffect(() => {
		if (!anchorEl) setLocalSettings(settings);
	}, [settings, anchorEl]);
	useEffect(() => setLocalViewStartDay(viewStartDay), [viewStartDay]);

	return (
		<>
			<IconButton
				data-testid={"settings-panel-open-button"}
				data-annie-target={annieTarget}
				onClick={(e) => setAnchorEl(e.currentTarget)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<Box
					component={motion.div}
					animate={{ rotate: isHovered ? 180 : 0 }}
					transition={{ duration: 0.5, ease: "easeInOut" }}
					sx={{ display: "flex", alignItems: "center" }}
				>
					<SettingsOutlined sx={{ color: "primary.main" }} />
				</Box>
			</IconButton>
			<Menu
				data-testid={"staffing-settings-panel"}
				open={!!anchorEl}
				onClose={closePanel}
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				slotProps={{ paper: { sx: { p: 2 } } }}
			>
				<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "24px" }}>Settings</Typography>
				{mode === "daily" && (
					<Box>
						<Typography sx={{ color: "primary.main", fontWeight: 600, fontSize: "18px", p: 1.5 }}>Day View</Typography>
						<Box data-testid={"daily-view-type-section"} sx={{ display: "flex", alignItems: "center", gap: 2, ml: 3, mt: 1 }}>
							<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>View type</Typography>
							<SystemFlatSwitch
								data-testid={"daily-view-type-switch"}
								options={[
									{ label: "Standard", value: DailyViewOption.standard },
									{ label: "Gaps", value: DailyViewOption.gaps },
								]}
								activeIndexOrValue={localSettings.dailyView}
								onSwitch={(option) => setLocalSettings({ ...localSettings, dailyView: option.value })}
							/>
						</Box>
						<Box data-testid={"hppd-separation-type-section"} sx={{ display: "flex", alignItems: "center", gap: 2, ml: 3, mt: 1 }}>
							<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>Separate HPPD by</Typography>
							<SystemFlatSwitch
								data-testid={"hppd-separation-type-switch"}
								options={[
									{ label: "None", value: HppdSeparationType.None },
									{ label: "Role", value: HppdSeparationType.Role, disabled: jobManagementEnabled },
									{ label: "Role Groups", value: HppdSeparationType.RoleGroups, disabled: jobManagementEnabled },
									...(enabledJobs.length || jobManagementEnabled ? [{ label: "Jobs", value: HppdSeparationType.Jobs }] : []),
								]}
								activeIndexOrValue={localSettings.hppdSeparationType}
								onSwitch={(option) => setLocalSettings({ ...localSettings, hppdSeparationType: option.value })}
							/>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 3 }}>
							<ToggleButton
								data-testid={"daily-overlap-shifts-toggle"}
								value="check"
								selected={localSettings.overlapShifts}
								onChange={() => setLocalSettings({ ...localSettings, overlapShifts: !localSettings.overlapShifts })}
								sx={{ width: "12px", height: "12px" }}
							>
								{localSettings.overlapShifts && <Check fontSize="small" />}
							</ToggleButton>
							<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>Show Overlapping Shifts</Typography>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 3, mt: 1 }}>
							<ToggleButton
								data-testid={"daily-hide-hppd-toggle"}
								value="check"
								selected={localSettings.hideHppdDailyView}
								onChange={() => setLocalSettings({ ...localSettings, hideHppdDailyView: !localSettings.hideHppdDailyView })}
								sx={{ width: "12px", height: "12px" }}
							>
								{localSettings.hideHppdDailyView && <Check fontSize="small" />}
							</ToggleButton>
							<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>{t("hide.hppd")}</Typography>
						</Box>
					</Box>
				)}
				{mode === "weekly" && (
					<Box>
						<Typography sx={{ color: "primary.main", fontWeight: 600, fontSize: "18px", p: 1.5 }}>Weekly View</Typography>
						<Box sx={{ gap: 2, ml: 3 }}>
							{isDynamicWeekStartEnabled && (
								<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
									<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>{t("schedule.template.week_start")}</Typography>
									<SystemDropdown
										items={weekStartDayOptions}
										selectedItem={localViewStartDay}
										onChange={(v) => v !== undefined && setLocalViewStartDay(v)}
										label={t("schedule.template.week_start")}
										formControlSx={{ minWidth: "140px" }}
										data-testid="staffing-settings-week-start"
									/>
								</Box>
							)}
							{weeklyGapsViewEnabled && localSettings.view === WeeklyViewOption.V2 ? (
								<Box>
									{!jobManagementEnabled && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 350 }}>
											<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>Grouping</Typography>
											<SystemFlatSwitch
												data-testid={"grouping-flat-switch"}
												options={[
													{ label: "Role", value: WeeklyGroupingType.Role, disabled: jobManagementEnabled },
													{ label: "Role Groups", value: WeeklyGroupingType.RoleGroups, disabled: jobManagementEnabled },
													...(enabledJobs.length || jobManagementEnabled ? [{ label: "Jobs", value: WeeklyGroupingType.Jobs }] : []),
												]}
												activeIndexOrValue={localSettings.weeklyGroupingType}
												onSwitch={(option) => setLocalSettings({ ...localSettings, weeklyGroupingType: option.value })}
											/>
										</Box>
									)}
									<Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
										<ToggleButton
											value="check"
											selected={localSettings.disableTimeGroupsOverlaps}
											onChange={() => setLocalSettings({ ...localSettings, disableTimeGroupsOverlaps: !localSettings.disableTimeGroupsOverlaps })}
											sx={{ width: "12px", height: "12px" }}
										>
											{localSettings.disableTimeGroupsOverlaps && <Check fontSize="small" />}
										</ToggleButton>
										<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>Disable Time Group Overlaps</Typography>
										<InfoIconWithTooltip title={t("disable.time.group.overlaps.tooltip")} />
									</Box>
								</Box>
							) : (
								<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
									<ToggleButton
										data-testid={"weekly-group-roles-toggle"}
										value="check"
										selected={localSettings.groupRoles}
										onChange={() => setLocalSettings({ ...localSettings, groupRoles: !localSettings.groupRoles })}
										sx={{ width: "12px", height: "12px" }}
									>
										{localSettings.groupRoles && <Check fontSize="small" />}
									</ToggleButton>
									<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>Group Roles</Typography>
								</Box>
							)}

							<Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
								<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>View type</Typography>
								{/* The walkthrough anchor sits on this wrapper, which contains nothing but the switch, so the
								    spotlight is the control itself. */}
								<Box
									data-annie-target="staffing-view-type"
									sx={{
										display: "flex",
										alignItems: "center",
									}}
								>
									{/* Which weekly GRID to show. The People roster is not a slot here: it is a view of the
									    Staffing area in its own right, chosen from the Day/Week/Month/People selector. */}
									<SystemFlatSwitch<WeeklyViewOption>
										options={[
											{ label: "Detailed", value: WeeklyViewOption.V1, "data-testid": "weekly-view-detailed-button" },
											{ label: weeklyGapsViewEnabled ? "Gaps" : "Summary", value: WeeklyViewOption.V2, "data-testid": "weekly-view-summary-button" },
										]}
										activeIndexOrValue={localSettings.view}
										onSwitch={(option) => setLocalSettings({ ...localSettings, view: option.value })}
										data-testid={"weekly-view-settings-section"}
									/>
								</Box>
							</Box>
						</Box>
					</Box>
				)}
				{mode === "daily" && (!!hospitalData.units?.length || !!hospitalData.floors?.length) && (
					<Box>
						<Divider sx={{ my: 1, pt: 2 }} />
						<Typography sx={{ color: "primary.main", fontWeight: 600, fontSize: "18px", p: 1.5 }}>Census By Location</Typography>
						<Box data-testid={"census-by-location-section"} sx={{ gap: 2, ml: 3 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
								<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>Group Census By</Typography>
								<SystemFlatSwitch
									data-testid={"census-location-mode-switch"}
									options={[
										{ label: "None", value: LocationMode.None },
										{ label: "Floor", value: LocationMode.Floor },
										{ label: "Unit", value: LocationMode.Unit },
										{ label: "Both", value: LocationMode.Both },
										{ label: "Auto", value: LocationMode.Auto },
									]}
									activeIndexOrValue={localSettings.censusByLocation}
									onSwitch={(option) => setLocalSettings({ ...localSettings, censusByLocation: option.value })}
								/>
							</Box>
						</Box>
					</Box>
				)}
				{saveAsDefaultEnabled && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1, mt: 2, borderTop: `1px solid ${INK.DISABLED}`, py: 1 }}>
						<ToggleButton
							data-testid={"save-as-default-toggle"}
							value="check"
							selected={saveAsDefault}
							onChange={() => setSaveAsDefault(!saveAsDefault)}
							sx={{ width: "12px", height: "12px" }}
						>
							{saveAsDefault && <Check fontSize="small" />}
						</ToggleButton>
						<Typography sx={{ color: "primary.main", fontWeight: 500, fontSize: "14px" }}>{t("settings.save.as.default")}</Typography>
					</Box>
				)}
				<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
					<SystemButton data-testid={"staffing-settings-cancel-button"} onClick={closePanel} buttonVariant={"secondary"} disabled={isLoading}>
						{t("cancel")}
					</SystemButton>
					<SystemButton data-testid={"staffing-settings-submit-button"} onClick={onSubmit} disabled={isLoading} loading={isLoading}>
						{t("submit")}
					</SystemButton>
				</Box>
			</Menu>
			{saveAsDefaultEnabled && (
				<SaveAsDefaultDialog
					open={showConfirmDialog}
					onClose={() => setShowConfirmDialog(false)}
					onConfirm={onConfirmSaveAsDefault}
				/>
			)}
		</>
	);
};
